// Recorte de itens por variedade e composição por classificação — puros, usados pelo SERVIDOR
// (que calcula os cards do topo) e pelo CLIENT (que desenha a linha da tabela).
//
// ⚠️ UMA função, dois lados. Duas implementações do mesmo recorte foi exatamente o que produziu
// o bug da venda 023/2026 antes do PR #77, e o comentário em `pesoMedioDaVenda`
// (client/src/pages/vendas.tsx) já avisa sobre isso. Se o topo e a linha discordarem do que é
// "a parte de Prata desta carga", volta-se ao mesmo lugar.

export interface ItemComProduto {
  produtoVenda: { variedadeId: string | null; classificacao?: string | null };
}

/**
 * Variedade recorta ITENS, não vendas.
 *
 * Período, comprador e situação selecionam VENDAS; variedade seleciona DENTRO da venda. Se ela
 * filtrasse vendas inteiras, a 034 apareceria em "Prata" trazendo junto os 1.056 kg de Nanica —
 * recriando pela quarta vez nesta feature o mesmo erro de misturar conjuntos. A pergunta é
 * "como está a Prata", e o kg de Nanica não faz parte da resposta.
 *
 * Venda sem nenhum item da variedade devolve `[]` — e some do recorte, porque não tem o que
 * dizer sobre ela. Sem filtro, devolve tudo.
 */
export function recortarItensPorVariedade<T extends ItemComProduto>(
  itens: T[],
  variedadeId?: string | null,
): T[] {
  if (!variedadeId) return itens;
  return itens.filter(it => it.produtoVenda.variedadeId === variedadeId);
}

/**
 * A carga inteira foi PESADA (peso variável) — nesse modo a caixa não é uma grandeza da venda,
 * e `total_caixas = 0` é um resultado LEGÍTIMO, nunca "ainda não calculado".
 *
 * ⚠️ `length > 0` faz parte do predicado e não é detalhe: carga vazia NÃO é carga em pesagem.
 * Sem isso, uma venda sem item nenhum esconderia o total geral do romaneio.
 *
 * Item sem `modoPeso` (venda anterior à coluna) conta como CAIXA_PADRAO — é o mesmo default
 * da migração 045 e do Zod.
 *
 * Mora aqui, em `shared/`, porque o servidor (romaneio, texto de WhatsApp) e o client (lista,
 * detalhe) precisam do MESMO predicado: duas implementações do mesmo recorte foi o que
 * produziu o bug da venda 023/2026 antes do #77.
 */
export function cargaEmPesagem<T extends { modoPeso?: string | null }>(itens: T[]): boolean {
  return itens.length > 0 && itens.every(i => i.modoPeso === "PESAGEM");
}

export interface ItemParaClassificacao {
  variedadeId: string | null;
  variedadeNome: string | null;
  /** `produtos_venda.classificacao` — "1ª"/"2ª"/"3ª" ou null. NUNCA derivar do nome do produto. */
  classificacao: string | null;
  kg: number;
  valor: number;
}

export interface ResumoClassificacao {
  porClasse: Array<{ classificacao: string; kg: number; valor: number }>;
  /** Denominador do percentual. É este, nunca o kg total do recorte. */
  kgClassificado: number;
  kgSemClassificacao: number;
  /** Nomes, para o rótulo do card — derivados do DADO, jamais chumbados em "Prata". */
  variedadesComClassificacao: string[];
  variedadesSemClassificacao: string[];
}

/**
 * Composição por classificação do recorte.
 *
 * ⚠️ A REGRA QUE NÃO PODE SER VIOLADA: o percentual é sempre sobre `kgClassificado`, nunca
 * sobre o kg total. Sobre o total, uma carga com Nanica aparece com "% de 1ª" falsamente
 * baixo — a venda 034 daria 39,4% em vez de 86,4%. O detalhe da venda já acerta isso
 * ("Distribuição: Prata 1ª 86,4%"); o card precisa acertar igual.
 *
 * E o corolário, que vale para qualquer indicador desta tela: TODO NÚMERO QUE EXCLUI PARTE DO
 * UNIVERSO DECLARA QUANTO EXCLUIU, no mesmo lugar onde aparece — daí `kgSemClassificacao` e
 * `variedadesSemClassificacao` saírem junto, e não como detalhe opcional. Cobertura implícita
 * foi o que escondeu o erro do Colhido em julho.
 *
 * As listas de variedades saem do dado observado: amanhã a Nanica pode passar a ser
 * classificada, ou entra uma terceira variedade, e o rótulo não pode mentir.
 */
export function resumirClassificacao(itens: ItemParaClassificacao[]): ResumoClassificacao {
  const porClasse = new Map<string, { kg: number; valor: number }>();
  // Por variedade: quanto tem de classificado e quanto de não-classificado.
  const porVariedade = new Map<string, { nome: string; kgComClasse: number; kgSemClasse: number }>();

  for (const it of itens) {
    const chaveVar = it.variedadeId ?? "__sem_variedade__";
    let bucketVar = porVariedade.get(chaveVar);
    if (!bucketVar) {
      bucketVar = { nome: it.variedadeNome ?? "Sem variedade", kgComClasse: 0, kgSemClasse: 0 };
      porVariedade.set(chaveVar, bucketVar);
    }

    const classe = it.classificacao;
    if (classe) {
      bucketVar.kgComClasse += it.kg;
      let bucketClasse = porClasse.get(classe);
      if (!bucketClasse) {
        bucketClasse = { kg: 0, valor: 0 };
        porClasse.set(classe, bucketClasse);
      }
      bucketClasse.kg += it.kg;
      bucketClasse.valor += it.valor;
    } else {
      bucketVar.kgSemClasse += it.kg;
    }
  }

  const variedadesComClassificacao: string[] = [];
  const variedadesSemClassificacao: string[] = [];
  let kgClassificado = 0;
  let kgSemClassificacao = 0;
  for (const b of Array.from(porVariedade.values())) {
    kgClassificado += b.kgComClasse;
    kgSemClassificacao += b.kgSemClasse;
    // Uma variedade conta como "com classificação" se QUALQUER item dela tem classe. Uma
    // variedade com parte classificada e parte não aparece nas duas listas de propósito: é
    // um cadastro incompleto, e escondê-lo faria a base parecer maior do que é.
    if (b.kgComClasse > 0) variedadesComClassificacao.push(b.nome);
    if (b.kgSemClasse > 0) variedadesSemClassificacao.push(b.nome);
  }

  // Ordem estável: 1ª, 2ª, 3ª… — ordena pelo rótulo, que já é numérico na prática.
  const listaClasses = Array.from(porClasse.entries())
    .map(([classificacao, b]) => ({ classificacao, kg: b.kg, valor: b.valor }))
    .sort((a, b) => a.classificacao.localeCompare(b.classificacao, "pt-BR", { numeric: true }));

  return {
    porClasse: listaClasses,
    kgClassificado,
    kgSemClassificacao,
    variedadesComClassificacao: variedadesComClassificacao.sort(),
    variedadesSemClassificacao: variedadesSemClassificacao.sort(),
  };
}

/** Percentual de uma classe sobre o universo CLASSIFICADO. `null` quando não há base — a tela
 *  troca o card por "Composição" em vez de mostrar 0% ou um traço. */
export function percentualDaClasse(resumo: ResumoClassificacao, classificacao: string): number | null {
  if (resumo.kgClassificado <= 0) return null;
  const classe = resumo.porClasse.find(c => c.classificacao === classificacao);
  return ((classe?.kg ?? 0) / resumo.kgClassificado) * 100;
}
